import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getPayoffPayments } from 'selectors/getPayoffPayments';
import { getApplicationData } from 'selectors/getApplicationData';
import { getPayoffPayments as getPayoffPaymentsThunk } from 'thunks';
import PaymentDetail, { PaymentDetailRow } from 'components/FinancialCheckup/PaymentDetail';
import { formatTextDate, formatYearMonthDayToDate } from 'utils/dateUtils';
import { PaymentStatus, PayoffPaymentResponse } from 'api/PayoffApi';
import { FlowComponentType } from 'routes/types';
import clsx from 'clsx';

import styles from './TransactionDetail.module.scss';

const formatPayoffStatus = (payoff: PayoffPaymentResponse): React.ReactNode => {
  const {
    status,
    payment: { estimatedCompletionDate },
  } = payoff;
  switch (status) {
    case PaymentStatus.PendingSubmission:
      return <p>Payment pending submission.</p>;
    case PaymentStatus.Pending:
      return 'Payment pending';
    case PaymentStatus.Processing:
      return `Payment processing.
      
      ${
        estimatedCompletionDate
          ? `Payment estimated to complete on ${formatTextDate(formatYearMonthDayToDate(estimatedCompletionDate)!)}`
          : ''
      }
        `;
    case PaymentStatus.Sent:
      return 'Payment sent';

    case PaymentStatus.Failed:
      return (
        <>
          <p className={clsx(styles.value, styles.failed)}>Payment failed.</p>
          <p className={styles.value}>
            Funds will be sent to your bank account so that you can pay your card yourself.
          </p>
        </>
      );
    default:
      return 'Unknown';
  }
};

const TransactionDetail = (props: FlowComponentType) => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { payments, isLoading } = useSelector(getPayoffPayments);
  const applicationData = useSelector(getApplicationData);
  const applicationId = applicationData.application?.id;

  const paymentResponse = payments.find(({ payment }) => payment.id === id);

  useEffect(() => {
    if (!paymentResponse && applicationId) {
      dispatch(getPayoffPaymentsThunk(applicationId));
    }
  }, [paymentResponse, applicationId]);

  const getPaymentDetailData = () => {
    if (!paymentResponse) return null;
    const {
      payment: { amount, estimatedCompletionDate: estimatedCompletionDateString, firm },
    } = paymentResponse;

    const estimatedCompletionDate = estimatedCompletionDateString
      ? formatYearMonthDayToDate(estimatedCompletionDateString)
      : null;

    const detailRows: PaymentDetailRow[] = [
      { title: 'Merchant', value: firm ?? 'Not available' },
      { title: 'Status', value: formatPayoffStatus(paymentResponse) },
      { title: 'Date', value: estimatedCompletionDate ? formatTextDate(estimatedCompletionDate) : 'Not available' },
    ];

    return {
      amount: amount / 100,
      detailRows,
    };
  };

  const paymentData = getPaymentDetailData();

  return (
    <PaymentDetail
      {...props}
      amount={paymentData?.amount ?? 0}
      detailRows={paymentData?.detailRows ?? []}
      isLoading={isLoading && !paymentResponse}
      error={!isLoading && !paymentResponse ? 'Payment not found' : undefined}
    />
  );
};

export default TransactionDetail;
